import React, { ReactNode, useMemo } from "react"

import styled from "styled-components"

import { SupportContacts, SupportLevel } from "@izettle/constants"
import { useLocalization } from "@izettle/lingo-client"
import { typography, colors, borderRadius, spacing } from "@izettle/otto"
import { Text } from "@izettle/react"

import { customInstructionKeyValue } from "./content"

const Card = styled.div`
  background-color: ${colors.background.default};
  border-radius: ${borderRadius.medium};
  padding: ${spacing.small};

  margin-bottom: ${spacing.small};

  &:last-child {
    margin-bottom: ${spacing.none};
  }
`

const List = styled.ul`
  margin-left: ${spacing.small};
`

const ListItem = styled.li`
  ${typography.regular}
`

const FootNote = styled(Text.Regular)`
  margin-top: ${spacing.small};
`

type InstructionProps = {
  instruction: string
  title: string
  introText: string
  listTitle: string
  listItems: string[] | undefined
  footNote: string
  rejectedReasons : string[]
}

export const Instruction: React.FC<InstructionProps> = ({
  instruction,
  title,
  introText,
  listTitle,
  listItems,
  footNote,
  rejectedReasons
}) => {
  const { text, isoLocale } = useLocalization()

  const contactSupportList: ReactNode = useMemo(() => {
    const contacts = SupportContacts.get(isoLocale)
    const phoneNumber = contacts?.phoneNumbers.get(SupportLevel.Standard)?.[0] ?? ""
    const email = contacts?.email

    return (
      <>
        <ListItem role="listitem" key={phoneNumber}>
          {phoneNumber}
        </ListItem>
        <ListItem role="listitem" key={email}>
          {email}
        </ListItem>
      </>
    )
  }, [])

  const customErrorKeyExist  = customInstructionKeyValue[instruction] && (rejectedReasons.includes(customInstructionKeyValue[instruction].errorKey) || 
    instruction === 'proofOfId'
  )

  return (
    <Card key={instruction}>
      <Text.RegularBold>{title}</Text.RegularBold>
      {
        customErrorKeyExist ? <ListItem role="listitem"> {text(customInstructionKeyValue[instruction].translationKey)}</ListItem> 
        :
        <>
          <Text.Regular>{introText}</Text.Regular>
          <Text.RegularSecondary>{listTitle}</Text.RegularSecondary>
          <List role="list">
            {instruction === "contactSupport"
              ? contactSupportList
              : 
              listItems?.map((listItem) => 
              <ListItem role="listitem" key={listItem}>
                {  text(listItem) } 
              </ListItem>
            )
              }
          </List>
        </>
      }
      {footNote !== "" && <FootNote>{footNote}</FootNote>}
    </Card>
  )
}
